import type { Messages } from './en';

/**
 * Arabic messages (RTL). Must provide every key defined in en.ts — enforced by the Messages type.
 * Brand product names (Johnson Window Films, Marathon, InsulatIR, Summit) are kept in Latin script
 * until approved Arabic forms are supplied.
 */
export const ar: Messages = {
  common: {
    backToSite: 'العودة إلى الموقع',
    stagingRibbon: 'نسخة مراجعة — المحتوى والوسائط بانتظار الاعتماد',
    brandName: 'القناع الشفاف',
    brandNameAr: 'القناع الشفاف',
    partnerBrand: 'Johnson Window Films',
    tagline: 'أفلام Johnson Window Films لمركبتك — اختيار وتركيب وتوثيق بعناية.',
    loading: 'جارٍ التحميل…',
    retry: 'حاول مرة أخرى',
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saved: 'تم الحفظ',
    cancel: 'إلغاء',
    continue: 'متابعة',
    back: 'رجوع',
    next: 'التالي',
    submit: 'إرسال',
    close: 'إغلاق',
    enlarge: 'تكبير',
    enlargePhoto: 'تكبير الصورة: {alt}',
    search: 'بحث',
    optional: 'اختياري',
    remove: 'حذف',
    edit: 'تعديل',
    add: 'إضافة',
    viewAll: 'عرض الكل',
    signIn: 'تسجيل الدخول',
    signOut: 'تسجيل الخروج',
    register: 'إنشاء حساب',
    myAccount: 'حسابي',
    language: 'اللغة',
    theme: 'وضع العرض',
    themeToDark: 'التبديل إلى الوضع الداكن',
    themeToLight: 'التبديل إلى الوضع الفاتح',
    skipToContent: 'تخطَّ إلى المحتوى الرئيسي',
    errorGeneric: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
    empty: 'لا يوجد شيء هنا بعد.',
    comingSoon: 'قريباً',
    notConfigured: {
      title: 'تعذّر تحميل هذه الخدمة حالياً',
      body: 'الخدمة غير متاحة مؤقتاً. يرجى المحاولة بعد قليل.',
    },
    status: {
      sample: 'عينة',
      demo: 'بيانات تجريبية',
      placeholder: 'عنصر مؤقت',
      integrationReady: 'جاهز للربط',
      requiresApi: 'يتطلب واجهة برمجية',
      requiresCredentials: 'يتطلب بيانات اعتماد',
      requiresBusinessConfirmation: 'يتطلب اعتماد الإدارة',
      draftContent: 'محتوى مبدئي — بانتظار الاعتماد',
      developmentVisual: 'عنصر مرئي للتطوير',
      prototypeReference: 'مرجع من النموذج الأولي',
    },
    counts: {
      branches: {
        zero: 'لا توجد فروع',
        one: 'فرع واحد',
        two: 'فرعان',
        few: '{wordsM} فروع',
        many: '{count} فرعًا',
        other: '{count} فرع',
      },
      cities: {
        zero: 'لا توجد مدن',
        one: 'مدينة واحدة',
        two: 'مدينتان',
        few: '{wordsF} مدن',
        many: '{count} مدينة',
        other: '{count} مدينة',
      },
    },
  },

  nav: {
    home: 'الرئيسية',
    vehicles: 'المركبات',
    services: 'الخدمات',
    centers: 'المراكز',
    products: 'أفلام Johnson',
    book: 'احجز',
    verify: 'التحقق',
    track: 'التتبع',
    warranty: 'الضمان',
    contact: 'تواصل معنا',
    story: 'قصتنا',
    authenticity: 'الأصالة',
    privacy: 'سياسة الخصوصية',
    credits: 'الإشارات والتراخيص',
    menu: 'القائمة',
    closeMenu: 'إغلاق القائمة',
    primary: 'التنقل الرئيسي',
  },

  landing: {
    hero: {
      eyebrow: 'Johnson Window Films',
      title: 'مركبتك.\nبضوءٍ مختلف.',
      subtitle: 'اكتشف أفلام Johnson Window Films لمركبتك، واختر الفيلم الذي يناسبك، واحجز موعد التركيب — كل ذلك في مكان واحد.',
      primaryCta: 'اختر مركبتك',
      secondaryCta: 'احجز زيارة',
      scrollHint: 'مرّر للاستكشاف',
      skipToVehicle: 'التخطي إلى اختيار مركبتك',
    },
    stage: {
      label: 'رحلتك',
      words: {
        vehicle: 'المركبة',
        glass: 'الزجاج',
        light: 'الضوء',
        protection: 'الحماية',
        transformation: 'التحوّل',
      },
      lines: {
        vehicle: 'كل شيء يبدأ بمركبتك.',
        glass: 'كل رحلة تُرى من خلال الزجاج.',
        light: 'كل يوم، تلتقي الشمس بزجاجك.',
        protection: 'فيلم Johnson، مُركَّب على زجاجك.',
        transformation: 'المركبة نفسها. بإطلالة جديدة.',
      },
      choose: 'اختر مركبتك',
      reduceMotion: 'تقليل الحركة',
      restoreMotion: 'إعادة تشغيل الحركة',
    },
    disclosure: {
      illustrativeEn: 'Illustrative Visualization',
      illustrativeAr: 'تصور توضيحي',
    },
    vehicles: {
      label: 'مركبتك',
      title: 'ابدأ بمركبتك',
      body: 'اختر الشركة المصنّعة والطراز وسنة الصنع، وسنحتفظ بها لك طوال خطوات الحجز.',
      cta: 'المتابعة إلى الحجز',
      chooseCta: 'اختر مركبتك',
      changeCta: 'تغيير المركبة',
    },
    stickyBook: {
      cta: 'احجز زيارة',
      noSelection: 'احجز موعد التركيب في دقائق',
      interestCount: '{count} مُختار',
    },
    services: {
      label: 'الخدمات',
      title: 'الخدمات',
      body: 'اختر الخدمة لتعرض مراكزنا والأوقات المتاحة.',
      cta: 'جميع الخدمات',
      empty: 'ستُعرض خدماتنا هنا قريباً.',
      book: 'احجز هذه الخدمة',
      duration: '{minutes} دقيقة',
    },
    families: {
      label: 'Johnson Window Films',
      title: 'عائلات المنتجات',
      body: 'اكتشف تشكيلة أفلام Johnson Window Films المتوفرة في مراكز القناع الشفاف.',
      pendingSpecs: 'اطلب التفاصيل الفنية الكاملة من فريقنا.',
      cta: 'استكشف جميع الأفلام',
      empty: 'ستُعرض تشكيلة الأفلام هنا قريباً.',
      interestCta: 'أنا مهتم',
      interested: 'أُضيف إلى زيارتك',
      details: 'التفاصيل',
    },
    book: {
      label: 'الحجز',
      title: 'احجز زيارتك',
      body: 'اختر الخدمة — مركبتك والأفلام التي اخترتها محفوظة لك.',
      selection: 'اختياراتك',
      noSelection: 'لم تختر مركبة أو فيلماً بعد. يمكنك إضافتهما أثناء الحجز.',
      change: 'تغيير',
      cta: 'المتابعة إلى الحجز',
    },
    installation: {
      label: 'التركيب',
      title: 'التركيب في مراكزنا',
      body: 'أحضر مركبتك إلى أحد مراكزنا، ويتولى فريقنا تركيب الفيلم الذي اخترته وتوثيق عملية التركيب لك.',
      cta: 'احجز زيارة',
    },
    verify: {
      label: 'التحقق',
      title: 'تحقّق مما على زجاجك',
      body: 'لكل عملية تركيب ملصق QR خاص بها. امسحه أو أدخل رمزه للاطلاع على سجل التركيب.',
    },
    warranty: {
      label: 'الضمان الرقمي',
      title: 'ضمانك معك دائماً',
      body: 'سجل ضمانك محفوظ في حسابك، جاهز متى احتجته — دون أوراق قد تضيع.',
      signedInCta: 'الانتقال إلى ضماني',
      signedOutCta: 'سجّل الدخول إلى ضمانك',
      infoCta: 'كيف يعمل الضمان الرقمي',
      chain: {
        title: 'من الفيلم إلى حسابك',
        product: 'منتج Johnson',
        center: 'مركز التركيب',
        qr: 'ملصق QR',
        record: 'السجل الرقمي',
        account: 'حسابك',
      },
    },
    why: {
      label: 'لماذا القناع الشفاف',
      title: 'كل ما تحتاجه في مكان واحد',
      items: [
        { title: 'اختيار بثقة', body: 'اطّلع على الأفلام والخدمات المناسبة لمركبتك قبل زيارتك.' },
        { title: 'حجز في دقائق', body: 'اختر المركز والوقت المناسبين لك عبر الإنترنت.' },
        { title: 'تحقّق في أي وقت', body: 'لكل عملية تركيب سجل يمكنك الرجوع إليه برمز QR.' },
        { title: 'ضمانك في حسابك', body: 'سجل ضمانك معك دائماً، لا في درج السيارة.' },
      ],
    },
    story: {
      label: 'قصتنا',
      title: 'القناع الشفاف',
      body: 'يقدّم القناع الشفاف أفلام Johnson Window Films للسائقين عبر رحلة واحدة بسيطة — اختر فيلمك، واحجز زيارتك، واحتفظ بسجل التركيب والضمان في حسابك.',
    },
    ctas: {
      booking: { title: 'احجز موعد التركيب', body: 'اختر المركز والوقت الذي يناسبك.', cta: 'احجز الآن' },
      verify: {
        title: 'تحقّق من منتج',
        body: 'أدخل الرمز المطبوع أسفل ملصق QR الخاص بك.',
        placeholder: 'رمز QR',
        cta: 'تحقّق',
      },
      warranty: { title: 'الضمان الرقمي', body: 'ستجد سجل ضمانك في حسابك.', cta: 'افتح ضماني' },
      tracking: {
        title: 'تتبّع شحنة',
        body: 'أدخل رمز التتبع لمعرفة مكان شحنتك.',
        placeholder: 'رمز التتبع',
        cta: 'تتبّع',
      },
      order: { title: 'حجوزاتك', body: 'سجّل الدخول لعرض زياراتك القادمة والسابقة.', cta: 'حجوزاتي' },
      register: {
        title: 'أنشئ حسابك',
        body: 'احفظ مركباتك، وأدِر حجوزاتك، واحتفظ بضمانك في مكان واحد.',
        cta: 'إنشاء حساب',
      },
    },
    contact: {
      label: 'الدعم',
      title: 'نحن هنا لمساعدتك',
      body: 'لديك سؤال عن الأفلام أو الحجز أو الضمان؟ فريقنا جاهز لمساعدتك.',
      cta: 'تواصل معنا',
    },
  },

  footer: {
    rights: '© {year} القناع الشفاف. جميع الحقوق محفوظة.',
    explore: 'استكشف',
    trust: 'الثقة والأصالة',
    account: 'الحساب',
    support: 'الدعم',
    label: 'روابط الموقع',
    languageLabel: 'اللغة',
  },

  auth: {
    login: {
      title: 'تسجيل الدخول',
      subtitle: 'مرحباً بعودتك. مركباتك وحجوزاتك وضمانك بانتظارك.',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      submit: 'تسجيل الدخول',
      forgot: 'نسيت كلمة المرور؟',
      noAccount: 'جديد هنا؟',
      createAccount: 'أنشئ حساباً',
      invalid: 'البريد الإلكتروني أو كلمة المرور غير صحيحة.',
      unverified: 'يرجى تأكيد بريدك الإلكتروني أولاً — ستجد الرابط في صندوق الوارد.',
    },
    register: {
      title: 'أنشئ حسابك',
      subtitle: 'احفظ مركباتك، واحجز أسرع، واحتفظ بضمانك في مكان واحد.',
      fullName: 'الاسم الكامل',
      mobile: 'رقم الجوال',
      mobileCountry: 'رمز الدولة',
      mobileHint: 'اختر الدولة ثم أدخل الرقم. في المملكة مثلًا: 05XXXXXXXX',
      email: 'البريد الإلكتروني',
      password: 'كلمة المرور',
      passwordHint: '١٠ أحرف على الأقل تتضمن حروفاً كبيرة وصغيرة ورقماً.',
      confirmPassword: 'تأكيد كلمة المرور',
      preferredLanguage: 'اللغة المفضلة',
      submit: 'إنشاء الحساب',
      haveAccount: 'لديك حساب بالفعل؟',
      vehicleNote: 'يمكنك إضافة مركباتك بعد إنشاء الحساب.',
      exists: 'قد يكون هناك حساب مسجّل بهذا البريد. جرّب تسجيل الدخول أو إعادة تعيين كلمة المرور.',
    },
    verifyEmail: {
      title: 'تحقّق من بريدك الإلكتروني',
      body: 'أرسلنا رابط التأكيد إلى {email}. افتحه لتفعيل حسابك.',
      bodyGeneric: 'أرسلنا إليك رابط التأكيد. افتحه لتفعيل حسابك.',
      backToLogin: 'العودة إلى تسجيل الدخول',
    },
    forgot: {
      title: 'إعادة تعيين كلمة المرور',
      body: 'أدخل بريدك الإلكتروني وسنرسل لك رابطاً لتعيين كلمة مرور جديدة.',
      submit: 'إرسال رابط إعادة التعيين',
      sent: 'إذا كان هناك حساب بهذا البريد، فرابط إعادة التعيين في طريقه إليك.',
    },
    reset: {
      requiredByAdmin: 'طلب مسؤول النظام تعيين كلمة مرور جديدة قبل المتابعة.',
      title: 'تعيين كلمة مرور جديدة',
      submit: 'تحديث كلمة المرور',
      done: 'تم تحديث كلمة المرور.',
      invalidLink: 'رابط إعادة التعيين غير صالح أو منتهي الصلاحية. اطلب رابطاً جديداً.',
    },
    callback: {
      working: 'جارٍ إكمال تسجيل الدخول…',
      failed: 'تعذّر إكمال تسجيل الدخول. ربما انتهت صلاحية الرابط.',
    },
    signedOut: 'تم تسجيل خروجك.',
    resend: 'إعادة إرسال رابط التأكيد',
    resent: 'إذا كان حسابك لا يزال بحاجة إلى تأكيد، فرابط جديد في طريقه إليك.',
    rateLimited: 'محاولات كثيرة. يرجى الانتظار دقيقة ثم المحاولة مرة أخرى.',
    goToLogin: 'الانتقال إلى تسجيل الدخول',
    newPassword: 'كلمة المرور الجديدة',
    confirmNewPassword: 'تأكيد كلمة المرور الجديدة',
    checkingLink: 'جارٍ التحقق من الرابط…',
    verifiedSignIn: 'إذا فتحت الرابط في متصفح آخر، فقد يكون بريدك مؤكداً بالفعل — سجّل الدخول للمتابعة.',
    reauthenticate: 'لحمايتك، سجّل الخروج ثم سجّل الدخول مجدداً، وبعدها غيّر كلمة المرور.',
    samePassword: 'اختر كلمة مرور مختلفة عن كلمة المرور الحالية.',
  },

  validation: {
    required: 'هذا الحقل مطلوب.',
    nameLength: 'يرجى إدخال اسمك الكامل (من ٢ إلى ١٥٠ حرفاً).',
    email: 'يرجى إدخال بريد إلكتروني صحيح.',
    mobile: 'يرجى إدخال رقم جوال صحيح.',
    passwordRules: 'استخدم ١٠ أحرف على الأقل تتضمن حروفاً كبيرة وصغيرة ورقماً.',
    passwordMismatch: 'كلمتا المرور غير متطابقتين.',
    vin: 'رقم الهيكل (VIN) مكوّن من ١٧ حرفاً ورقماً (دون I أو O أو Q).',
  },

  vehicles: {
    selector: {
      legend: 'مركبتك',
      make: 'الشركة المصنّعة',
      model: 'الطراز',
      year: 'سنة الصنع',
      trim: 'الفئة',
      choose: 'اختر…',
      loading: 'جارٍ التحميل…',
      noData: 'تعذّر عرض المركبات حالياً.',
      sampleNote: 'تحتوي قائمة المركبات على سجلات عيّنة لأغراض العرض.',
      steps: { make: 'الشركة', model: 'الطراز', year: 'السنة', trim: 'الفئة' },
      chooseMake: 'اختر الشركة المصنّعة',
      chooseModel: 'اختر طراز {make}',
      chooseYear: 'ما سنة صنع {model}؟',
      chooseTrim: 'ما الفئة؟',
      skipTrim: 'لست متأكداً — تخطَّ',
      searchMakes: 'ابحث عن الشركة',
      searchModels: 'ابحث عن الطراز',
      noMatch: 'لا توجد نتائج. جرّب كتابة مختلفة.',
      allTypes: 'الكل',
      change: 'تغيير',
      yourVehicle: 'مركبتك',
      missingVehicle: 'لم تجد مركبتك؟ تابع على أي حال — وسيؤكدها فريقنا معك.',
      bodyTypes: {
        sedan: 'سيدان',
        suv: 'دفع رباعي',
        coupe: 'كوبيه',
        hatchback: 'هاتشباك',
        pickup: 'بيك أب',
        van: 'فان',
        convertible: 'قابلة للطي',
        wagon: 'ستيشن',
        other: 'مركبة',
      },
    },
    explorer: {
      title: 'اعثر على مركبتك',
      body: 'اختر مركبتك وسنحتفظ بها لك خلال الحجز والتركيب.',
      selected: 'المركبة المختارة',
      compatibilityPending: 'سيؤكد فريقنا الأفلام المناسبة لمركبتك خلال زيارتك.',
      bookCta: 'احجز لهذه المركبة',
    },
    mine: {
      title: 'مركباتي',
      empty: 'لم تُضف أي مركبة بعد.',
      add: 'إضافة مركبة',
      addTitle: 'إضافة مركبة',
      vin: 'رقم الهيكل (VIN)',
      plate: 'رقم اللوحة',
      color: 'اللون',
      nickname: 'اسم مميّز',
      saveVehicle: 'حفظ المركبة',
      removeConfirm: 'هل تريد حذف هذه المركبة؟',
      primary: 'الأساسية',
    },
  },

  catalog: {
    servicesTitle: 'الخدمات',
    servicesBody: 'كل ما نقدّمه في فروعنا — اختر الخدمة واحجز زيارتك.',
    familiesTitle: 'Johnson Window Films',
    familiesBody: 'اكتشف تشكيلة الأفلام المتوفرة في مراكزنا، وسيساعدك فريقنا في الاختيار.',
    specsPending: 'التفاصيل الفنية عند الطلب',
    priceOnRequest: 'السعر عند الطلب',
    from: 'ابتداءً من {price}',
    family: {
      notFoundTitle: 'لم نعثر على هذه العائلة',
      notFoundBody: 'لم نتمكن من العثور على هذا الفيلم. تصفّح المجموعة الكاملة بدلًا من ذلك.',
      backToFamilies: 'كل عائلات الأفلام',
      seeOnVehicle: 'شاهدها على مركبتك',
      bookWithThisFilm: 'احجز بهذا الفيلم',
      figuresTitle: 'الأداء المقاس',
      figuresNote: 'من ورقة المواصفات المعتمدة، ويُعرض كل رقم على حدة.',
      shadesTitle: 'درجات التعتيم المتاحة',
      specsTitle: 'المواصفات',
      glassTitle: 'زجاجًا بعد زجاج',
      glassBody: 'يُركَّب الفيلم على كل جزء من الزجاج على حدة. اختر المناطق لمركبتك — ويؤكّد فريقنا الفيلم المناسب لكلٍّ منها قبل التركيب.',
      servicesTitle: 'خدمات التركيب',
      servicesBody: 'اختر الخدمة التي تغطي الزجاج الذي تريد معالجته، ثم الفرع والموعد.',
      originalTitle: 'أصلي، ومسجَّل، ويمكن التحقق منه',
      originalBody: 'يصل فيلم Johnson في كرتونته الأصلية، وعليها ملصقات تحمل رمز المنتج ورقم الدفعة والرقم التسلسلي. ويُسجَّل كل تركيب: فيبقى ملصق QR وضمانك الرقمي مع مركبتك.',
      originalVerify: 'تحقّق من رمز QR',
      originalLearn: 'كيف تتعرّف على الفيلم الأصلي',
      warrantyLink: 'الضمان',
      othersTitle: 'عائلات Johnson الأخرى',
      allFamilies: 'كل عائلات الأفلام',
      finalTitle: 'شاهد {name} على مركبتك',
      finalBody: 'اختر مركبتك لتعاين الفيلم زجاجًا بعد زجاج، أو احجز التركيب في فرع قريب منك.',
    },
  },

  viewer: {
    eyebrow: 'معاينة العازل',
    title: 'شاهد العازل على مركبتك',
    subtitle: 'دوّر المركبة وقرّبها، واختر الزجاج وعائلة الفيلم ودرجة التظليل.',
    chooseVehicle: 'اختر مركبتك أولًا',
    changeVehicle: 'تغيير المركبة',
    service: 'الخدمة',
    glass: 'الزجاج',
    zones: {
      windshield: 'الزجاج الأمامي',
      front_left: 'النافذة الأمامية اليسرى',
      front_right: 'النافذة الأمامية اليمنى',
      rear_left: 'النافذة الخلفية اليسرى',
      rear_right: 'النافذة الخلفية اليمنى',
      rear_quarter_left: 'الزجاج الجانبي الخلفي الأيسر',
      rear_quarter_right: 'الزجاج الجانبي الخلفي الأيمن',
      rear_windshield: 'الزجاج الخلفي',
      sunroof: 'فتحة السقف / السقف البانورامي',
    },
    areas: {
      windshield: 'الزجاج الأمامي',
      frontSides: 'الزجاج الجانبي الأمامي',
      rearSides: 'الزجاج الجانبي الخلفي',
      rearGlass: 'الزجاج الخلفي',
      roof: 'السقف البانورامي / فتحة السقف',
    },
    areaNoFilm: 'دون فيلم',
    areaNotInService: 'غير مشمول في هذه الخدمة',
    planTitle: 'خطة الفيلم لمركبتك',
    planEmpty: 'اختر الزجاج الذي تريد تغطيته.',
    windshieldSeparate: 'يُختار الزجاج الأمامي دائمًا بشكل مستقل، ويُؤكَّد المسموح به معك في المركز.',
    family: 'عائلة الفيلم',
    anyFamily: 'لا تفضيل بعد',
    shade: 'درجة التظليل',
    shades: { light: 'فاتح', medium: 'متوسط', dark: 'داكن', deep: 'داكن جدًا' },
    reflectance: 'الانعكاس',
    reflectances: { low: 'منخفض', medium: 'متوسط', high: 'مرتفع' },
    reflectanceApproved: 'من المواصفات المعتمدة.',
    illustrativeGeneric: 'درجات التظليل والانعكاس هنا توضيحية وليست أرقام منتج.',
    illustrativeFamily: 'لم تُنشر بعد الأرقام المعتمدة لعائلة الفيلم هذه، لذلك تستخدم المعاينة درجات توضيحية.',
    views: 'زوايا العرض',
    presets: {
      front_three_quarter: 'الأمام',
      side: 'الجانب',
      rear_three_quarter: 'الخلف',
      top: 'السقف',
    },
    controlsHint: 'اسحب للتدوير · استخدم إصبعين أو عجلة الفأرة للتكبير',
    loading3d: 'جارٍ تحميل العرض ثلاثي الأبعاد…',
    needsCapableDevice: 'يحتاج العرض ثلاثي الأبعاد إلى جهاز يدعم WebGL2، لذلك نعرض صورة المركبة.',
    book: 'احجز هذه الخدمة',
    previewLink: 'عاين العازل على هذه المركبة',
  },

  home: {
    hero: {
      eyebrow: 'Johnson Window Films · القناع الشفاف',
      title: 'لا يكفي اسم جونسون…\nتأكد من المصدر.',
      subtitle: 'فيلم أصلي، وتركيب احترافي، وضمان رقمي — كل تركيب مسجَّل ويمكن تتبّعه حتى مركبتك.',
      primaryCta: 'اختر مركبتك',
      secondaryCta: 'تحقّق من الأصالة',
      scrollHint: 'مرّر للأسفل',
      vehicleCredit: 'النموذج ثلاثي الأبعاد: {credit}',
      footageCredit: 'التصوير: {credit}',
      play: 'تشغيل',
      pause: 'إيقاف مؤقت',
      scenesNav: 'مشاهد القصة',
      vehicleAlt: '{vehicle} على طريق مفتوح',
      creditsLink: 'الإشارات والتراخيص',
    },
    scenes: {
      drive: 'على الطريق',
      sun: 'الشمس والحرارة',
      film: 'الفيلم',
      protection: 'أصلي ويمكن تتبّعه',
      choose: 'مركبتك',
    },
    sun: {
      label: '02 · الشمس والحرارة',
      title: 'أشعة الشمس أكثر من مجرد ضوء',
      body: 'تحمل أشعة الشمس الواصلة إلى سيارتك ضوءًا مرئيًا وحرارة الأشعة تحت الحمراء والأشعة فوق البنفسجية — وأول ما تلاقيه هو الزجاج. ويُختار فيلم النوافذ بحسب تعامله مع كلٍّ منها.',
      visible: { title: 'الضوء المرئي', body: 'ما تراه عبر الزجاج.' },
      infrared: { title: 'الأشعة تحت الحمراء', body: 'الجزء من أشعة الشمس الذي تشعر به حرارةً.' },
      ultraviolet: { title: 'الأشعة فوق البنفسجية', body: 'إشعاع غير مرئي من الشمس.' },
    },
    film: {
      label: '03 · الفيلم',
      title: 'فيلم يُركَّب زجاجًا بعد زجاج',
      body: 'لكل جزء من الزجاج معالجته المستقلة — وأنت تختار أين يُركَّب الفيلم وأي فيلم من Johnson.',
      zones: {
        windshield: 'الزجاج الأمامي',
        frontSides: 'الزجاج الجانبي الأمامي',
        rearSides: 'الزجاج الجانبي الخلفي',
        rearGlass: 'الزجاج الخلفي',
        roof: 'السقف البانورامي / فتحة السقف',
      },
      note: 'تعتمد المناطق المتاحة على مركبتك والخدمة التي تختارها.',
      cta: 'عاين الفيلم على مركبتك',
    },
    protection: {
      label: '04 · أصلي ويمكن تتبّعه',
      title: 'فيلم أصلي، يمكن تتبّعه حتى مركبتك',
      body: 'يُسجَّل كل تركيب كسلسلة واحدة — من منتج الفيلم إلى الطقم أو اللفة، والدفعة، والفرع الذي نفّذ التركيب، وملصق QR، وضمانك الرقمي.',
      verifyCta: 'تحقّق من رمز QR',
      learnCta: 'كيف تتعرّف على الفيلم الأصلي',
    },
    original: {
      label: 'فيلم Johnson الأصلي',
      title: 'اعرف الأصلي قبل تركيبه',
      body: 'يصل الفيلم الأصلي في كرتونة Johnson Window Films، وعلى جوانبها ملصقات تحمل رمز المنتج ورقم الدفعة والرقم التسلسلي. وبعد التركيب، يؤكّد ملصق QR على مركبتك السجلّ.',
      learnCta: 'كيف تتعرّف على الفيلم الأصلي',
      verifyCta: 'تحقّق من رمز QR',
    },
    choose: {
      label: '05 · مركبتك',
      title: 'ابدأ بمركبتك',
      body: 'اختر الشركة المصنّعة والطراز وسنة الصنع، وتبقى مركبتك معك في المعاينة والحجز.',
      cta: 'افتح تجربة المركبة',
    },
    films: {
      label: 'أفلام Johnson',
      title: 'ثلاث عائلات من أفلام Johnson',
      body: 'Marathon وInsulatIR وSummit — تعرّف على كل عائلة، وشاهدها على مركبتك، واحجز التركيب.',
      detailsCta: 'التفاصيل',
      previewCta: 'شاهدها على مركبتك',
      compareCta: 'كل عائلات الأفلام',
    },
    network: {
      label: 'شبكة الفروع',
      title: '{branchesPhrase} في {citiesPhrase}',
      titleFallback: 'فروعنا',
      body: 'من الرياض وجدة ومكة المكرمة إلى المنطقة الشرقية والشمال والجنوب والدوحة — اعثر على أقرب فرع إليك.',
      cta: 'افتح خريطة الفروع',
      citiesLabel: 'المدن',
    },
  },

  story: {
    eyebrow: 'قصتنا',
    title: 'رحلة بدأت عام {year}',
    subtitle: 'بدأت الرحلة بالعمل باليد… واليوم تتحوّل عقود من الخبرة الميدانية إلى منظومة رقمية متكاملة.',
    heroCaption: 'صورة تاريخية — أحمد الشريف الحسيني أثناء التركيب بنفسه.',
    chaptersLabel: 'محطات الرحلة',
    chapters: {
      start: {
        year: '1999',
        title: 'جونسون في المملكة',
        body: 'انطلاق العمل بأفلام جونسون في المملكة، وبناء العلاقة الأولى مع السوق.',
      },
      handsOn: {
        year: 'البداية',
        title: 'العمل باليد وبناء المشروع من الصفر',
        body: 'أحمد الشريف الحسيني يعمل بنفسه على السيارات ويطوّر خبرته ميدانيًا، تركيبًا بعد تركيب.',
      },
      training: {
        year: 'التدريب',
        title: 'بناء فرق التركيب وتطويرها',
        body: 'تدريب الفنيين ونقل الخبرة العملية إلى فرق تركيب منظّمة.',
      },
      ceramic: {
        year: '2006',
        title: 'النانو سيراميك والعناية الاحترافية',
        body: 'توسيع نطاق الخدمات ليشمل حماية النانو سيراميك والعناية الاحترافية بالمركبات.',
      },
      ppf: {
        year: '2014',
        title: 'أفلام حماية الطلاء',
        body: 'إضافة خط أفلام حماية الطلاء (PPF).',
      },
      today: {
        year: 'اليوم',
        title: 'منظومة تشغيل رقمية متكاملة',
        body: 'عقود من الخبرة الميدانية تصبح منظومة واحدة تتتبّع كل طلب حتى لفّته وطقمه وفرعه وضمانه.',
      },
    },
    videoLabel: 'بكلماته',
    videoTitle: 'فيلم مع أحمد',
    videoBody: 'فيلم قصير يرافق القصة المكتوبة أعلاه.',
    craftTitle: 'الخبرة التي لا تُشترى… تُبنى',
    craftBody: 'من التركيب باليد إلى شبكة من الفروع، وفنيين مدرَّبين، ومنظومة تتبّع تربط كل طلب باللفة والطقم والضمان.',
    traceTitle: 'من اللفة إلى ضمانك الرقمي',
    traceBody: 'العناية نفسها تعيش اليوم في المنصة: كل تركيب مسجَّل، ويبقى ملصق QR والضمان الرقمي مع مركبتك.',
    sourcesTitle: 'المصادر',
    sourcesBody: 'التواريخ والمحطات كما زوّدنا بها المالك (سبتمبر 2026).',
    sourcesPhoto: 'صورة أحمد صورة تاريخية من أرشيف المالك، معروضة كاملة دون قص.',
    ctaBook: 'احجز موعد تركيب',
    ctaAuthenticity: 'كيف تعمل الأصالة',
  },

  films: {
    eyebrow: 'أفلام Johnson',
    title: 'عائلات أفلام Johnson',
    subtitle: 'Marathon وInsulatIR وSummit. افتح أي عائلة لتفاصيلها، أو شاهدها على مركبتك قبل الحجز.',
    metrics: {
      vlt: 'نفاذية الضوء المرئي (VLT)',
      tser: 'إجمالي الطاقة الشمسية المرفوضة (TSER)',
      ir: 'الأشعة تحت الحمراء (IR / SIRR)',
      uv: 'رفض الأشعة فوق البنفسجية',
      glare: 'تقليل الوهج',
    },
    explainTitle: 'ماذا تعني الأرقام',
    explainBody: 'تُوصف أفلام النوافذ بقياسات منفصلة، ويُعرض كل قياس منها على حدة — دون دمجها في رقم واحد.',
    explain: {
      vlt: 'مقدار الضوء المرئي الذي يمر عبر الزجاج مع الفيلم. كلما قلّت القيمة كان الفيلم أغمق.',
      tser: 'مقدار ما يُحجب من إجمالي طاقة الشمس — الضوء والحرارة والأشعة فوق البنفسجية معًا.',
      ir: 'مقدار ما يُحجب من الأشعة تحت الحمراء، وهي الجزء الذي تشعر به حرارةً من أشعة الشمس.',
      uv: 'مقدار ما يُحجب من الأشعة فوق البنفسجية.',
      glare: 'مقدار تقليل وهج الضوء الساطع.',
    },
    approvedNote: 'الأرقام من أوراق المواصفات المعتمدة.',
    compareTitle: 'جنبًا إلى جنب',
    chooseFor: 'شاهدها على مركبتك',
    details: 'التفاصيل',
    range: '{from}–{to}%',
    percent: '{value}%',
  },

  privacy: {
    eyebrow: 'الخصوصية',
    title: 'سياسة الخصوصية',
    intro: 'في جونسون، نراعي خصوصية جميع المستخدمين، ونلتزم بحماية جميع المعلومات التي تُجمع عبر الموقع، مثل:',
    items: {
      registration: 'جميع المعلومات الشخصية التي تُجمع أثناء إجراءات التسجيل؛',
      orders: 'جميع تفاصيل طلبات الشراء وسجلات المشتريات؛',
      delivery: 'جميع معلومات التوصيل وبيانات التواصل العامة.',
    },
    retain: 'نود أن نطمئنك بأننا نحتفظ بهذه البيانات لتمكيننا من تنفيذ طلباتك وتلبية احتياجاتك بوجه عام. كما نتخذ جميع الخطوات اللازمة لحماية معلوماتك الشخصية من الوصول أو الاستخدام أو الإفصاح غير المصرّح به، أو من التلف.',
    disclose: 'سنفصح عن معلوماتك الشخصية بالقدر الضروري المعقول لتلبية طلبك أو لتنفيذ طلب أو عملية شراء، بما في ذلك على سبيل المثال لا الحصر: تزويد شركة الشحن أو التوصيل بعنوانك، أو إرسال معلوماتك إلى مؤسسة مصرفية أو إلى مزوّد بوابة الدفع لدينا للموافقة على معاملتك. ومع ذلك، يُرجى العلم بأنه إذا طلبت منا أي جهة تنظيمية أو سلطة حكومية تحقق في نشاط مشبوه تقديم بياناتك الشخصية أو معلومات المستخدم، فيحق لنا ذلك.',
    marketing: 'وقد نستخدم معلوماتك الشخصية أيضًا لإرسال نشراتنا الإخبارية وتفاصيل العروض الخاصة، أو العروض الترويجية أو عروض المنتجات التي قد تهمك.',
    source: 'النص مقدَّم من المالك (سياسة الخصوصية في jwfarabia.com، سبتمبر 2026). الترجمة العربية وملاءمة النص لهذه المنصة بانتظار المراجعة القانونية.',
  },

  credits: {
    eyebrow: 'الإشارات',
    title: 'الإشارات والتراخيص',
    intro: 'الوسائط التابعة لجهات أخرى المعروضة في هذا الموقع، مع مالكها ومصدرها وترخيصها والتعديلات التي أجريناها عليها.',
    modelTitle: '{name} — نموذج ثلاثي الأبعاد',
    owner: 'المالك',
    source: 'المصدر',
    licence: 'الترخيص',
    status: 'الحالة',
    temporary: 'مركبة مؤقتة للعرض التجريبي ريثما يتوفر أصل مرخّص ومعتمد لرينج روفر، وهي المركبة المعتمدة للإطلاق المطلوبة.',
    use: 'أين يظهر',
    useBody: 'يظهر في الصفحة الرئيسية لبيئة العرض التجريبي (وليس بيئة الإنتاج) كبديل مؤقت، ويُستبدل تلقائيًا بمجرد تسجيل أصل معتمد لرينج روفر.',
    changes: 'التعديلات التي أُجريت',
    trademark: 'BMW وX5 علامتان تجاريتان لشركة BMW AG، وتُستخدمان هنا فقط لتعريف طراز المركبة المعروضة. ولا ترعى BMW القناع الشفاف ولا تؤيده ولا تشاركه.',
    licenceNote: 'يسمح ترخيص CC BY 4.0 بمشاركة النموذج وتعديله، مع الإشارة إلى المصدر ورابط الترخيص وبيان التعديلات التي أُجريت.',
  },

  authenticity: {
    eyebrow: 'الأصالة',
    title: 'كيف تتعرّف على فيلم Johnson الأصلي',
    subtitle: 'ما الذي تتحقق منه قبل التركيب وبعده — وما الذي يمكنك التحقق منه بنفسك اليوم.',
    stepsTitle: 'عشرة أشياء تتحقق منها',
    stepsIntro: 'من الكرتون المختوم إلى ضمانك الرقمي: كل عنصر أدناه جزء من سجل التركيب الأصلي.',
    steps: {
      carton: { title: 'الكرتون الأصلي', body: 'يُخرَج الفيلم من كرتون Johnson Window Films الأصلي. اطلب رؤيته قبل بدء التركيب.' },
      innerBox: { title: 'صندوق الحماية الداخلي', body: 'داخل الكرتون، تُحفظ اللفة أو الطقم في صندوق الحماية الداخلي.' },
      roll: { title: 'تغليف الفيلم واللفة', body: 'تبقى اللفة أو الطقم المقصوص مسبقًا في تغليفه الأصلي حتى يُفتح لمركبتك.' },
      sideLabels: { title: 'الملصقات الجانبية', body: 'يحمل الكرتون والصندوق ملصقاتهما الأصلية على الجوانب.' },
      johnsonCode: { title: 'كود جونسون', body: 'يحدد كود المنتج على الملصق الفيلم بدقة.' },
      batch: { title: 'رقم الدفعة', body: 'يربط رقم الدفعة العبوة بدفعة إنتاجها.' },
      serial: { title: 'الرقم التسلسلي أو رقم الطقم', body: 'لكل لفة أو طقم رقمه التسلسلي أو رقم طقمه الخاص.' },
      qr: { title: 'ملصق QR', body: 'يحصل تركيبك على ملصق QR — الرمز الوحيد الذي يمكن لأي شخص التحقق منه عبر الإنترنت.' },
      center: { title: 'فرع التركيب', body: 'يُسجَّل الفرع الذي ركّب الفيلم ضمن سجل التركيب.' },
      warranty: { title: 'الضمان الرقمي', body: 'يُسجَّل ضمانك رقميًا ويبقى في حسابك.' },
    },
    publicTitle: 'ما يمكنك التحقق منه عبر الإنترنت اليوم',
    publicBody: 'رمز QR على ملصق تركيبك هو الرمز الوحيد الذي يمكن لأي شخص التحقق منه عبر الإنترنت. أما أرقام الأطقم وأكواد الأفلام وأرقام الدفعات والأرقام التسلسلية وأرقام الطلبات فتبقى في سجل تركيبك الخاص.',
    publicCheckable: 'يمكن التحقق منه عبر الإنترنت',
    privateRecord: 'في سجلك الخاص',
    photosTitle: 'العبوة الأصلية',
    photosBody: 'صور من فروعنا لمكونات المنتج الأصلي.',
    chainTitle: 'سجل التركيب، رمزًا برمز',
    chainIntro: 'هذه هي الرموز التي تحفظها المنصة لكل تركيب. رمز QR وحده يمكن التحقق منه للعموم، وتبقى البقية خاصة بك وبالمركز الذي نفّذ التركيب.',
    items: {
      film: { name: 'رمز الفيلم', what: 'رمز منتج أفلام Johnson الذي تم تركيبه.' },
      kit: { name: 'رقم الطقم أو اللفة', what: 'العبوة (طقم أو لفة) التي أُخذ منها الفيلم.' },
      batch: { name: 'الدفعة والرقم التسلسلي', what: 'دفعة تلك العبوة ورقمها التسلسلي كما استُلمت.' },
      center: { name: 'مركز التركيب', what: 'المركز الذي ركّب الفيلم.', where: 'في حسابك، ضمن سجل الضمان.' },
      order: { name: 'رقم الطلب', what: 'رقم طلبك لدينا.' },
      qr: { name: 'رمز QR', what: 'رمز فريد على ملصق QR الخاص بتركيبك.', where: 'تحقّق منه في صفحة التحقق — دون عرض أي بيانات شخصية.' },
      warranty: { name: 'الضمان الرقمي', what: 'رقم ضمانك وحالته، مرتبطان بالتركيب.', where: 'في حسابك، ضمن «ضماني».' },
    },
    recordedWhere: 'مسجَّل مع تركيبك.',
    orderPrivate: 'لا يمكن البحث عنه للعموم أبدًا، حمايةً لخصوصيتك.',
    liveVerify: 'تحقّق من رمز QR',
    liveAccount: 'افتح ضماني',
    verifyOtherCodes: 'لديك رمز مختلف؟ تعرّف على معنى كل رمز في سجلك.',
  },

  centers: {
    title: 'فروعنا',
    subtitle: '{branchesPhrase} في {citiesPhrase}. اعثر على أقرب فرع، واحصل على الاتجاهات أو اتصل مسبقًا.',
    subtitleFallback: 'اعثر على أقرب فرع، واحصل على الاتجاهات أو اتصل مسبقًا.',
    filterLabel: 'المدينة',
    allCities: 'كل المدن',
    empty: 'لا توجد فروع لعرضها بعد.',
    addressLabel: 'العنوان',
    phoneLabel: 'الهاتف',
    call: 'اتصل',
    servicesLabel: 'الخدمات',
    availabilityLabel: 'الحجز الإلكتروني',
    nextSlot: 'أقرب موعد متاح: {time}',
    noSlotSoon: 'لا توجد مواعيد إلكترونية خلال الأسبوعين القادمين.',
    bookingNotOpen: 'الحجز الإلكتروني غير متاح بعد — اتصل بالفرع أو تواصل معنا.',
    book: 'احجز هنا',
    directions: 'الاتجاهات',
    showOnMap: 'اعرض على الخريطة',
    nearMe: 'الأقرب إليّ',
    locating: 'جارٍ تحديد موقعك…',
    locationDenied: 'لم يُسمح بالوصول إلى موقعك. اختر مدينة بدلًا من ذلك.',
    locationUnavailable: 'تعذّر تحديد موقعك الآن. اختر مدينة بدلًا من ذلك.',
    distanceKm: 'على بُعد {km} كم (خط مستقيم)',
    youAreHere: 'موقعك',
    nearestFound: 'أقرب فرع: {name}',
    mapLabel: 'خريطة فروع القناع الشفاف',
    mapUnavailable: 'الخريطة التفاعلية غير متاحة هنا. جميع الفروع مدرجة أدناه.',
    mapKeyHint: 'الخريطة التفاعلية غير مهيأة: أضف VITE_MAPTILER_KEY (docs/phase-3-staging-apply.md).',
    sourceNote: 'المواقع من قائمة الفروع الرسمية ومن الإدراج العام لكل فرع على الخرائط، وتم التحقق منها في {date}.',
    listingNameDiffers: 'مدرج على الخريطة باسم تجاري مختلف.',
    noPublicListing: 'لا يوجد إدراج عام على الخرائط — الموقع من قائمة الفروع الرسمية.',
    reset: 'كل الفروع',
    listCount: {
      zero: 'لا توجد فروع',
      one: 'فرع واحد',
      two: 'فرعان',
      few: '{wordsM} فروع',
      many: '{count} فرعًا',
      other: '{count} فرع',
    },
    resultsFor: {
      zero: 'لا توجد فروع في {city}',
      one: 'فرع واحد في {city}',
      two: 'فرعان في {city}',
      few: '{wordsM} فروع في {city}',
      many: '{count} فرعًا في {city}',
      other: '{count} فرع في {city}',
    },
  },

  booking: {
    title: 'احجز زيارة',
    subtitle: 'اختر الخدمة والمركز والوقت. لن يستغرق الأمر أكثر من دقيقتين.',
    steps: {
      service: 'الخدمة',
      schedule: 'المركز والموعد',
      account: 'الحساب',
      vehicle: 'المركبة',
      review: 'المراجعة',
    },
    chooseService: 'اختر الخدمة',
    chooseCenter: 'المركز',
    chooseDate: 'التاريخ',
    chooseTime: 'الأوقات المتاحة',
    noTimes: 'لا توجد أوقات متاحة في هذا التاريخ. جرّب يوماً آخر.',
    noTimesShort: 'لا توجد أوقات',
    nextAvailable: 'أقرب موعد متاح: {date}',
    noAvailability: 'لا توجد أوقات متاحة في هذا المركز خلال الأسبوعين القادمين. جرّب مركزاً آخر أو تواصل معنا.',
    signInToContinue: 'سجّل الدخول أو أنشئ حساباً مجانياً لتأكيد حجزك. اختياراتك محفوظة.',
    chooseVehicle: 'أي مركبة؟',
    addVehicleInline: 'إضافة مركبة جديدة',
    myVehicles: 'مركباتي',
    noVehiclesYet: 'لم تُضِف مركبة بعد.',
    continueWithoutVehicle: 'المتابعة دون مركبة',
    vehicleNotRequired: 'هذه الخدمة لا تتطلب مركبة.',
    notes: 'ملاحظات للمركز',
    review: 'راجع حجزك',
    confirm: 'تأكيد الحجز',
    confirming: 'جارٍ التأكيد…',
    successTitle: 'وصلنا طلب حجزك',
    successBody: 'الحجز رقم {number} بانتظار تأكيد المركز. سنُعلمك فور تأكيده.',
    viewBookings: 'عرض حجوزاتي',
    unavailable: 'الحجز الإلكتروني متوقف مؤقتاً. تواصل معنا وسنحجز لك موعدك.',
    centersNotBookable: 'فروعنا غير متاحة للحجز الإلكتروني بعد — مواعيد عملها قيد التأكيد. اتصل بأقرب فرع وسيحجز لك موعدك.',
    callBranch: 'اتصل بـ{name}',
    allBranches: 'اعثر على أقرب فرع',
    stagingTestTitle: 'بيئة المراجعة: جرّب مسار الحجز كاملًا',
    stagingTestBody: 'تُتاح الفروع الحقيقية للحجز الإلكتروني بعد التحقق من مواعيد عملها. استخدم مركز الاختبار المُعلَّم — ليس موقعًا حقيقيًا ولا يظهر في الموقع المباشر أبدًا.',
    stagingTestCta: 'استخدم مركز الاختبار',
    slotTaken: 'حُجز هذا الوقت للتو. يرجى اختيار وقت آخر.',
    chooseAnotherTime: 'اختر وقتاً آخر',
    back: 'رجوع',
    continueCta: 'متابعة',
    change: 'تغيير',
    dayParts: {
      morning: 'صباحاً',
      afternoon: 'ظهراً',
      evening: 'مساءً',
    },
    interests: {
      title: 'اهتمامك',
      note: 'سيؤكد المركز الفيلم النهائي معك.',
      remove: 'إزالة',
    },
    summary: {
      service: 'الخدمة',
      center: 'المركز',
      time: 'التاريخ والوقت',
      vehicle: 'المركبة',
      noVehicle: 'لم تُحدَّد مركبة',
    },
  },

  bookingStatus: {
    pending: 'قيد الانتظار',
    confirmed: 'مؤكد',
    rescheduled: 'أُعيدت جدولته',
    cancelled: 'ملغى',
    completed: 'مكتمل',
  },

  verify: {
    title: 'تحقّق من التركيب',
    body: 'أدخل الرمز الموجود على ملصق QR للاطلاع على سجل التركيب والضمان.',
    code: 'رمز QR',
    submit: 'تحقّق',
    valid: 'تم العثور على السجل',
    invalid: 'لم نعثر على سجل لهذا الرمز. تأكّد منه وحاول مرة أخرى، أو تواصل معنا للمساعدة.',
    product: 'المنتج',
    family: 'عائلة المنتج',
    warrantyStatus: 'حالة الضمان',
    warrantyPeriod: 'مدة الضمان',
    installedOn: 'تاريخ التركيب',
    help: 'الرمز مطبوع أسفل ملصق QR على زجاجك، ويمكنك أيضاً مسح الملصق بكاميرا جوالك.',
    accountCta: 'عرض ضماني',
    contactCta: 'تواصل معنا',
    warrantyStates: {
      pending_activation: 'بانتظار التفعيل',
      active: 'ساري',
      expired: 'منتهٍ',
      void: 'ملغى',
    },
  },

  track: {
    title: 'تتبّع شحنتك',
    body: 'أدخل رمز التتبع الموجود في رسالتك لمعرفة آخر حالة.',
    code: 'رمز التتبع',
    submit: 'تتبّع',
    notFound: 'لم نعثر على شحنة بهذا الرمز. تأكّد منه وحاول مرة أخرى.',
    status: 'الحالة',
    timeline: 'مراحل الشحنة',
    expected: 'الوصول المتوقع: {date}',
    deliveredOn: 'سُلّمت في {date}',
    myShipments: 'شحناتي',
    help: 'ستجد رمز التتبع في الرسالة التي أرسلناها عند حجز الشحنة. تحتاج مساعدة؟ تواصل معنا.',
    statuses: {
      created: 'تم الحجز',
      scheduled: 'موعد الاستلام محدد',
      picked_up: 'تم الاستلام',
      in_transit: 'في الطريق',
      arrived: 'وصلت',
      delivered: 'تم التسليم',
      cancelled: 'ملغاة',
      exception: 'تحتاج إلى متابعة — سيتواصل معك فريقنا',
    },
  },

  warrantyPage: {
    howTitle: 'كيف يعمل',
    steps: [
      { title: 'التركيب في مركزنا', body: 'يتولى فريقنا تركيب الفيلم الذي اخترته ويوثّق عملية التركيب.' },
      { title: 'ملصق QR الخاص بك', body: 'يربط ملصق QR زجاجك بسجل التركيب الخاص به.' },
      { title: 'الضمان في حسابك', body: 'يُحفظ سجل ضمانك في حسابك — راجعه في أي وقت.' },
    ],
    title: 'الضمان الرقمي',
    body: 'عند اكتمال التركيب، يُنشأ سجل ضمانك ويُحفظ في حسابك، ويمكنك الرجوع إليه في أي وقت برمز QR. تخضع شروط الضمان لسياسة الضمان الرسمية للفيلم.',
    verifyCta: 'تحقّق برمز QR',
    accountCta: 'فتح حسابي',
  },

  contactPage: {
    optionsTitle: 'كيف يمكننا مساعدتك؟',
    options: {
      branches: { title: 'اتصل بأحد الفروع', body: 'أرقام هواتف الفروع واتجاهات الوصول إليها في صفحة الفروع.', cta: 'اعثر على فرع' },
      book: { title: 'احجز زيارة', body: 'اختر الخدمة والمركز والوقت عبر الإنترنت.', cta: 'احجز الآن' },
      bookings: { title: 'حجوزاتك', body: 'اعرض زيارتك القادمة أو ألغِها.', cta: 'حجوزاتي' },
      verify: { title: 'تحقّق من التركيب', body: 'راجع سجل التركيب والضمان الخاص بك.', cta: 'تحقّق' },
      track: { title: 'تتبّع شحنة', body: 'اعرف آخر حالة لشحنتك.', cta: 'تتبّع' },
    },
    title: 'نحن هنا لمساعدتك',
    body: 'لديك سؤال عن الأفلام أو الحجز أو الضمان؟ تواصل مع فريقنا.',
    channelsPending: 'تُنشر قنوات التواصل المركزية (الهاتف، البريد الإلكتروني، واتساب) بعد تأكيدها من الإدارة.',
  },

  errors: {
    notFoundTitle: 'الصفحة غير موجودة',
    notFoundBody: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
    forbiddenTitle: 'غير مصرّح بالدخول',
    forbiddenBody: 'لا يملك حسابك صلاحية الوصول إلى هذا القسم.',
    goHome: 'الذهاب إلى الرئيسية',
    goPortal: 'الذهاب إلى بوابتي',
    routeErrorTitle: 'حدث خطأ ما',
    routeErrorBody: 'حدث خطأ غير متوقع أثناء تحميل هذه الصفحة.',
    reload: 'إعادة تحميل الصفحة',
  },
};
